import type {
  DecisioningCriterionCheck,
  DecisioningRuleEvaluation,
  DecisionRulesEvaluation
} from '@prescreen/lib/loan-decision-rules'
import { Badge } from '@/components/ui/badge'
import { Check, X } from 'lucide-react'

type DecisionRulesEvaluationProps = {
  evaluation: DecisionRulesEvaluation
  reasons?: string[]
}

export function DecisionRulesEvaluationDetails({
  evaluation,
  reasons
}: DecisionRulesEvaluationProps) {
  const hasChecks = [...evaluation.knockouts, ...evaluation.combinations].some(
    rule => (rule.checks?.length ?? 0) > 0
  )

  return (
    <div className="mt-4 space-y-3 border-t pt-4">
      <DecisionRuleGroup title="Automatic denials" rules={evaluation.knockouts} />
      <DecisionRuleGroup title="Qualifying paths" rules={evaluation.combinations} />
      {!hasChecks && reasons && reasons.length > 0 && <DecisionReasons reasons={reasons} />}
    </div>
  )
}

function DecisionRuleGroup({
  title,
  rules
}: {
  title: string
  rules: DecisioningRuleEvaluation[]
}) {
  if (rules.length === 0) {
    return null
  }
  return (
    <section>
      <h5 className="mb-2 text-sm font-medium">{title}</h5>
      <div className="space-y-2">
        {rules.map(rule => (
          <div
            key={rule.id}
            className="flex items-start justify-between gap-3 rounded border p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{rule.name}</p>
              <CriterionChecks checks={rule.checks} />
              {!rule.checks?.length && rule.reasons.length > 0 && (
                <ul className="mt-1 list-disc list-inside text-xs text-gray-600">
                  {rule.reasons.map((reason, index) => (
                    <li key={`${index}:${reason}`}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
            <Badge variant={rule.result === 'pass' ? 'default' : 'destructive'}>
              {rule.result === 'pass' ? 'Pass' : 'Fail'}
            </Badge>
          </div>
        ))}
      </div>
    </section>
  )
}

function CriterionChecks({ checks }: { checks?: DecisioningCriterionCheck[] }) {
  if (!checks || checks.length === 0) {
    return null
  }
  return (
    <ul className="mt-2 space-y-1">
      {checks.map(check => (
        <li key={`${check.label}:${check.detail}`} className="flex items-start gap-1.5 text-xs">
          {check.result === 'pass' ? (
            <Check className="mt-0.5 h-3 w-3 shrink-0 text-green-600" />
          ) : (
            <X className="mt-0.5 h-3 w-3 shrink-0 text-red-600" />
          )}
          <span>
            <span className="font-medium text-gray-800">{check.label}: </span>
            <span className="text-gray-600">{check.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

export function DecisionReasons({ reasons }: { reasons: string[] }) {
  return (
    <div>
      <p className="text-sm font-medium">Evaluation reasons</p>
      <ul className="mt-1 list-disc list-inside text-xs text-gray-600">
        {reasons.map((reason, index) => (
          <li key={`${index}:${reason}`} className="flex items-start gap-1">
            <X className="mt-0.5 h-3 w-3 shrink-0 text-red-600" />
            <span>{reason}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
